/**
 * ReleasesPage Component
 * 
 * Displays the release history for a clinic
 * Allows users to:
 * - View all releases
 * - See release details
 * - Revert to previous releases
 * - Publish new releases (via modal)
 */

import { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Card, Button, Badge } from '../components/ui'
import api from '../services/api'
import { useAuthStore } from '../stores/authStore'
import useDraftStore from '../stores/draftStore'
import PublishReleaseModal from '../components/PublishReleaseModal'
import RevertReleaseModal from '../components/RevertReleaseModal'
import { releaseCounts } from '../utils/releaseCounts'

export default function ReleasesPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [releases, setReleases] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [revertingId, setRevertingId] = useState(null)
  const [isPublishModalOpen, setIsPublishModalOpen] = useState(false)
  const [revertModalRelease, setRevertModalRelease] = useState(null)
  
  const urlClinicId = searchParams.get('clinic') || 'tmates'
  const { user, currentClinicId, clinics } = useAuthStore()
  const clinicId = currentClinicId || urlClinicId
  const hasClinicSelected = !!currentClinicId
  const clinicDisplayName = clinics.find(c => c.id === clinicId)?.displayName || clinicId
  const { getCount, initialize } = useDraftStore()

  // Roles that can publish/revert releases (excludes viewer and data_entry)
  const canManageReleases = user && ['super_admin', 'clinic_admin', 'physician_manager', 'pharmacy_manager', 'program_manager'].includes(user.role)

  // Get active release
  const activeRelease = releases.find(r => r.is_active)
  
  // Initialize draft store
  useEffect(() => {
    if (user && clinicId) {
      const userId = user.id // Backend uses 'id', not '_id'
      const { userId: storeUserId, clinicId: storeClinicId, isInitialized } = useDraftStore.getState()
      
      // Re-initialize if not initialized OR if clinic/user changed
      if (!isInitialized || storeUserId !== userId || storeClinicId !== clinicId) {
        console.log('[RELEASES PAGE] Re-initializing draft store due to clinic/user change:', { clinicId, userId, oldClinicId: storeClinicId, oldUserId: storeUserId })
        initialize(clinicId, userId)
      }
    }
  }, [user, clinicId, initialize])
  
  // Load releases when clinic changes
  useEffect(() => {
    if (!hasClinicSelected) {
      setReleases([])
      setLoading(false)
      return
    }

    const loadReleases = async () => {
      try {
        setLoading(true)
        setError(null)

        console.log('[RELEASES PAGE] Loading releases for clinic:', clinicId)

        const response = await api.get(`/${clinicId}/releases`)
        setReleases(response.data.releases || [])

        console.log('[RELEASES PAGE] Loaded releases:', response.data.releases?.length || 0)
      } catch (err) {
        console.error('[RELEASES] Error loading releases:', err)
        setError(err.response?.data?.message || 'Failed to load releases')
      } finally {
        setLoading(false)
      }
    }

    loadReleases()
  }, [clinicId, hasClinicSelected])
  
  const loadReleases = async () => {
    try {
      setLoading(true)
      setError(null)
      
      console.log('[RELEASES PAGE] Reloading releases for clinic:', clinicId)
      
      const response = await api.get(`/${clinicId}/releases`)
      setReleases(response.data.releases || [])
      
      console.log('[RELEASES PAGE] Reloaded releases:', response.data.releases?.length || 0)
    } catch (err) {
      console.error('[RELEASES] Error loading releases:', err)
      setError(err.response?.data?.message || 'Failed to load releases')
    } finally {
      setLoading(false)
    }
  }
  
  const handleRevert = async (releaseId, releaseNumber) => {
    try {
      setRevertingId(releaseId)
      
      await api.post(`/${clinicId}/releases/${releaseId}/revert`)
      
      alert('Successfully reverted to previous release')
      loadReleases()
    } catch (err) {
      console.error('[RELEASES] Error reverting release:', err)
      alert(err.response?.data?.message || 'Failed to revert release')
    } finally {
      setRevertingId(null)
    }
  }
  
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A'
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }
  
  // Require a specific clinic to be selected
  if (!hasClinicSelected) {
    return (
      <div className="container mx-auto p-6">
        <div>
          <h1 className="text-3xl font-bold">Releases</h1>
          <p className="text-muted-foreground mt-1">
            View and manage configuration releases
          </p>
        </div>
        <Card className="p-12 mt-6">
          <div className="text-center">
            <h3 className="text-lg font-semibold mb-2">Select a Clinic</h3>
            <p className="text-muted-foreground">
              Releases are clinic-specific. Please select a clinic from the dropdown above to view and manage releases.
            </p>
          </div>
        </Card>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="container mx-auto p-6">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-lg text-muted-foreground">Loading releases...</div>
        </div>
      </div>
    )
  }
  
  if (error) {
    return (
      <div className="container mx-auto p-6">
        <Card className="p-6">
          <div className="text-destructive">
            <h3 className="text-lg font-semibold mb-2">Error Loading Releases</h3>
            <p>{error}</p>
            <Button 
              onClick={loadReleases} 
              className="mt-4"
            >
              Try Again
            </Button>
          </div>
        </Card>
      </div>
    )
  }
  
  return (
    <div className="container mx-auto p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-3xl font-bold">Releases</h1>
            <Badge variant="outline" className="text-sm">{clinicDisplayName}</Badge>
          </div>
          <p className="text-muted-foreground mt-1">
            View and manage configuration releases
          </p>
        </div>
        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={loadReleases}
          >
            Refresh
          </Button>
          {canManageReleases && (
            <Button
              onClick={() => setIsPublishModalOpen(true)}
              disabled={getCount() === 0}
            >
              Publish Release ({getCount()} changes)
            </Button>
          )}
        </div>
      </div>
      
      {/* No releases message */}
      {releases.length === 0 && (
        <Card className="p-12">
          <div className="text-center">
            <h3 className="text-lg font-semibold mb-2">No Releases Yet</h3>
            <p className="text-muted-foreground mb-4">
              {canManageReleases
                ? 'Make some changes and publish your first release'
                : 'No releases have been published yet'}
            </p>
            {canManageReleases && (
              <Button
                onClick={() => setIsPublishModalOpen(true)}
                disabled={getCount() === 0}
              >
                Publish Release
              </Button>
            )}
          </div>
        </Card>
      )}
      
      {/* Releases list */}
      <div className="space-y-4">
        {releases.map((release) => (
          <Card key={release._id} className="p-6">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                {/* Release header */}
                <div className="flex items-center gap-3 mb-2">
                  <h3 className="text-xl font-semibold">
                    Release #{release.release_number}: {release.release_name}
                  </h3>
                  <span className="px-2 py-1 text-xs font-medium bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-100 rounded-full">
                    {clinicDisplayName}
                  </span>
                  {release.is_active && (
                    <span className="px-2 py-1 text-xs font-medium bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-100 rounded-full">
                      ACTIVE
                    </span>
                  )}
                  {release.revert_of && (
                    <span className="px-2 py-1 text-xs font-medium bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-100 rounded-full">
                      REVERT
                    </span>
                  )}
                </div>
                
                {/* Release info */}
                <div className="text-sm text-muted-foreground space-y-1">
                  <div>
                    <span className="font-medium">By:</span> {release.created_by?.name || 'Unknown'} ({release.created_by?.email})
                  </div>
                  <div>
                    <span className="font-medium">Published:</span> {formatDate(release.published_at)}
                  </div>
                  {release.description && (
                    <div>
                      <span className="font-medium">Description:</span> {release.description}
                    </div>
                  )}
                </div>
                
                {/* Metadata. Releases published before metadata was stored
                    correctly have none at all, so this renders nothing for
                    them rather than a row of zeros. */}
                <div className="mt-3 flex flex-wrap gap-4 text-sm">
                  {releaseCounts(release).map(({ key, label, count }) => (
                    <div key={key} className="flex items-center gap-1">
                      <span className="font-medium">{count}</span>
                      <span className="text-muted-foreground">{label}</span>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Actions */}
              {canManageReleases && (
                <div className="flex flex-col gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setRevertModalRelease(release)}
                    disabled={release.is_active || revertingId === release._id}
                  >
                    {revertingId === release._id ? 'Reverting...' : 'Revert to This'}
                  </Button>
                </div>
              )}
            </div>
          </Card>
        ))}
      </div>
      
      {/* Publish modal */}
      <PublishReleaseModal
        isOpen={isPublishModalOpen}
        onClose={() => setIsPublishModalOpen(false)}
        onSuccess={() => loadReleases()}
        clinicId={clinicId}
      />
      
      {/* Revert modal */}
      <RevertReleaseModal
        isOpen={!!revertModalRelease}
        onClose={() => setRevertModalRelease(null)}
        onConfirm={() => handleRevert(revertModalRelease._id, revertModalRelease.release_number)}
        release={revertModalRelease}
        activeRelease={activeRelease}
      />
    </div>
  )
}

